import { motion, AnimatePresence } from 'framer-motion'
import { useEffect } from 'react'
import { X } from 'lucide-react'
import { useT, useLanguage } from '../i18n/LanguageContext'

interface Props {
  isOpen: boolean
  onClose: () => void
}

export default function PrivacyModal({ isOpen, onClose }: Props) {
  const t = useT()
  const { language } = useLanguage()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      window.addEventListener('keydown', onKey)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="privacy-title"
            className="relative w-full max-w-3xl max-h-[85vh] bg-[#0a0a0f] border border-white/[0.08] rounded-2xl flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/[0.06] shrink-0">
              <h2
                id="privacy-title"
                className="font-display text-xl font-bold text-white"
              >
                {t.privacy.title}
              </h2>
              <button
                onClick={onClose}
                aria-label={t.privacy.closeAria}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/[0.06] transition"
              >
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto p-6 text-sm text-zinc-300 leading-relaxed">
              {language === 'en' ? <PrivacyContentEN /> : <PrivacyContentPL />}
            </div>

            <div className="p-4 border-t border-white/[0.06] text-xs text-zinc-500 text-center shrink-0">
              {t.privacy.lastUpdated}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="mb-6 last:mb-0">
      <h3 className="font-display text-base font-semibold text-white mb-2">
        {title}
      </h3>
      <div className="text-zinc-400">{children}</div>
    </section>
  )
}

function PrivacyContentPL() {
  return (
    <>
      <p className="text-zinc-500 mb-6 text-xs">
        Niniejsza polityka opisuje, jakie dane osobowe są przetwarzane
        w związku z korzystaniem ze strony{' '}
        <span className="text-zinc-300">solidlabs.site</span>, na jakich
        podstawach prawnych i jakie prawa przysługują użytkownikom
        zgodnie z RODO (Rozporządzenie UE 2016/679).
      </p>

      <Section title="1. Administrator danych osobowych">
        Administratorem Twoich danych osobowych jest{' '}
        <strong className="text-white">Solid Labs PSA</strong> z siedzibą
        w Krakowie, ul. Mogilska 43, 31-545 Kraków.
        <br />
        Kontakt w sprawach związanych z ochroną danych:{' '}
        <a
          href="mailto:hello@solidlabs.site"
          className="text-brand-400 hover:text-white transition"
        >
          hello@solidlabs.site
        </a>
        .
      </Section>

      <Section title="2. Zakres przetwarzanych danych">
        W związku z korzystaniem ze strony przetwarzamy:
        <ul className="list-disc list-inside mt-2 space-y-1 text-zinc-400">
          <li>
            <span className="text-zinc-300">Dane techniczne</span> — adres
            IP, typ przeglądarki, system operacyjny, czas wizyty
            (zapisywane w logach serwera).
          </li>
          <li>
            <span className="text-zinc-300">Dane kontaktowe</span> —
            jeżeli wyślesz do nas wiadomość e-mail, przetwarzamy adres
            e-mail i treść korespondencji.
          </li>
          <li>
            <span className="text-zinc-300">Dane z cookies</span> —
            niezbędne pliki cookies do działania strony (np. zapamiętanie
            preferencji języka).
          </li>
        </ul>
      </Section>

      <Section title="3. Cele i podstawy prawne przetwarzania">
        <ul className="list-disc list-inside mt-1 space-y-2 text-zinc-400">
          <li>
            <span className="text-zinc-300">Obsługa korespondencji</span>{' '}
            — podstawa prawna: art. 6 ust. 1 lit. f RODO (uzasadniony
            interes administratora — odpowiadanie na zapytania).
          </li>
          <li>
            <span className="text-zinc-300">
              Zapewnienie bezpieczeństwa i działania strony
            </span>{' '}
            — podstawa prawna: art. 6 ust. 1 lit. f RODO (uzasadniony
            interes — utrzymanie infrastruktury, ochrona przed nadużyciami).
          </li>
          <li>
            <span className="text-zinc-300">
              Realizacja obowiązków prawnych
            </span>{' '}
            — np. odpowiedzi na żądania organów państwowych — podstawa
            prawna: art. 6 ust. 1 lit. c RODO.
          </li>
        </ul>
      </Section>

      <Section title="4. Okres przechowywania danych">
        <ul className="list-disc list-inside mt-1 space-y-1 text-zinc-400">
          <li>Korespondencja e-mail: do 24 miesięcy od ostatniego kontaktu.</li>
          <li>Logi serwera: do 12 miesięcy.</li>
          <li>
            Cookies niezbędne: czas sesji lub do momentu wyczyszczenia
            przez użytkownika w przeglądarce.
          </li>
        </ul>
      </Section>

      <Section title="5. Odbiorcy danych">
        Dane mogą być przekazywane następującym kategoriom odbiorców:
        <ul className="list-disc list-inside mt-2 space-y-1 text-zinc-400">
          <li>Dostawcom usług hostingowych i infrastruktury IT.</li>
          <li>Dostawcom usług poczty elektronicznej.</li>
          <li>
            Organom państwowym, jeśli wynika to z obowiązujących
            przepisów prawa.
          </li>
        </ul>
        Dane nie są sprzedawane ani udostępniane podmiotom trzecim
        w celach marketingowych.
      </Section>

      <Section title="6. Przekazywanie danych poza EOG">
        Część naszych dostawców usług (m.in. chmurowych) może mieć
        siedzibę poza Europejskim Obszarem Gospodarczym. W takich
        przypadkach przekazywanie odbywa się na podstawie standardowych
        klauzul umownych zatwierdzonych przez Komisję Europejską, które
        zapewniają odpowiedni poziom ochrony danych.
      </Section>

      <Section title="7. Twoje prawa">
        Zgodnie z RODO masz prawo do:
        <ul className="list-disc list-inside mt-2 space-y-1 text-zinc-400">
          <li>dostępu do swoich danych osobowych (art. 15 RODO),</li>
          <li>sprostowania danych (art. 16),</li>
          <li>usunięcia danych — „prawo do bycia zapomnianym" (art. 17),</li>
          <li>ograniczenia przetwarzania (art. 18),</li>
          <li>przenoszenia danych (art. 20),</li>
          <li>wniesienia sprzeciwu wobec przetwarzania (art. 21),</li>
          <li>cofnięcia udzielonej zgody w dowolnym momencie,</li>
          <li>
            wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych
            (ul. Stawki 2, 00-193 Warszawa).
          </li>
        </ul>
        Aby skorzystać z któregokolwiek z powyższych praw, skontaktuj
        się z nami przez{' '}
        <a
          href="mailto:hello@solidlabs.site"
          className="text-brand-400 hover:text-white transition"
        >
          hello@solidlabs.site
        </a>
        .
      </Section>

      <Section title="8. Pliki cookies">
        Strona wykorzystuje wyłącznie niezbędne pliki cookies, służące
        zapewnieniu poprawnego działania serwisu (np. zapamiętanie
        preferencji języka). Nie wykorzystujemy plików cookies do celów
        marketingowych ani profilowania. Możesz zarządzać cookies
        w ustawieniach swojej przeglądarki.
      </Section>

      <Section title="9. Brak zautomatyzowanego podejmowania decyzji">
        Twoje dane osobowe nie są przetwarzane w sposób
        zautomatyzowany, w tym nie podlegają profilowaniu.
      </Section>

      <Section title="10. Zmiany polityki prywatności">
        Zastrzegamy sobie prawo do wprowadzania zmian w niniejszej
        polityce. Aktualna wersja jest zawsze dostępna pod tym samym
        adresem URL. O istotnych zmianach informujemy z odpowiednim
        wyprzedzeniem.
      </Section>
    </>
  )
}

function PrivacyContentEN() {
  return (
    <>
      <p className="text-zinc-500 mb-6 text-xs">
        This policy describes what personal data is processed in connection
        with the use of the{' '}
        <span className="text-zinc-300">solidlabs.site</span> website, on
        what legal grounds, and what rights users have under the GDPR
        (EU Regulation 2016/679).
      </p>

      <Section title="1. Personal data controller">
        The controller of your personal data is{' '}
        <strong className="text-white">Solid Labs PSA</strong>, with its
        registered office at Mogilska 43, 31-545 Krakow, Poland.
        <br />
        Contact for data protection matters:{' '}
        <a
          href="mailto:hello@solidlabs.site"
          className="text-brand-400 hover:text-white transition"
        >
          hello@solidlabs.site
        </a>
        .
      </Section>

      <Section title="2. Scope of processed data">
        When using our website, we process:
        <ul className="list-disc list-inside mt-2 space-y-1 text-zinc-400">
          <li>
            <span className="text-zinc-300">Technical data</span> — IP
            address, browser type, operating system, visit time (stored
            in server logs).
          </li>
          <li>
            <span className="text-zinc-300">Contact data</span> — if you
            send us an email, we process your email address and the
            content of your correspondence.
          </li>
          <li>
            <span className="text-zinc-300">Cookie data</span> — essential
            cookies necessary for the site to function (e.g., remembering
            language preferences).
          </li>
        </ul>
      </Section>

      <Section title="3. Purposes and legal bases of processing">
        <ul className="list-disc list-inside mt-1 space-y-2 text-zinc-400">
          <li>
            <span className="text-zinc-300">Correspondence handling</span>{' '}
            — legal basis: Article 6(1)(f) GDPR (legitimate interest of
            the controller — responding to inquiries).
          </li>
          <li>
            <span className="text-zinc-300">
              Ensuring site security and operation
            </span>{' '}
            — legal basis: Article 6(1)(f) GDPR (legitimate interest —
            infrastructure maintenance, protection against abuse).
          </li>
          <li>
            <span className="text-zinc-300">
              Fulfilling legal obligations
            </span>{' '}
            — e.g., responding to requests from state authorities — legal
            basis: Article 6(1)(c) GDPR.
          </li>
        </ul>
      </Section>

      <Section title="4. Data retention period">
        <ul className="list-disc list-inside mt-1 space-y-1 text-zinc-400">
          <li>Email correspondence: up to 24 months from last contact.</li>
          <li>Server logs: up to 12 months.</li>
          <li>
            Essential cookies: session duration or until cleared by the
            user in their browser.
          </li>
        </ul>
      </Section>

      <Section title="5. Data recipients">
        Data may be shared with the following categories of recipients:
        <ul className="list-disc list-inside mt-2 space-y-1 text-zinc-400">
          <li>Hosting providers and IT infrastructure providers.</li>
          <li>Email service providers.</li>
          <li>State authorities, if required by applicable law.</li>
        </ul>
        Data is not sold or shared with third parties for marketing
        purposes.
      </Section>

      <Section title="6. Data transfers outside the EEA">
        Some of our service providers (including cloud providers) may be
        located outside the European Economic Area. In such cases,
        transfers are carried out based on Standard Contractual Clauses
        approved by the European Commission, which ensure an adequate
        level of data protection.
      </Section>

      <Section title="7. Your rights">
        Under the GDPR, you have the right to:
        <ul className="list-disc list-inside mt-2 space-y-1 text-zinc-400">
          <li>access your personal data (Article 15 GDPR),</li>
          <li>rectify data (Article 16),</li>
          <li>erase data — "right to be forgotten" (Article 17),</li>
          <li>restrict processing (Article 18),</li>
          <li>data portability (Article 20),</li>
          <li>object to processing (Article 21),</li>
          <li>withdraw consent at any time,</li>
          <li>
            lodge a complaint with the President of the Personal Data
            Protection Office (Stawki 2, 00-193 Warsaw, Poland).
          </li>
        </ul>
        To exercise any of the above rights, contact us at{' '}
        <a
          href="mailto:hello@solidlabs.site"
          className="text-brand-400 hover:text-white transition"
        >
          hello@solidlabs.site
        </a>
        .
      </Section>

      <Section title="8. Cookies">
        The website uses only essential cookies, necessary for proper
        site operation (e.g., remembering language preferences). We do
        not use cookies for marketing or profiling purposes. You can
        manage cookies in your browser settings.
      </Section>

      <Section title="9. No automated decision-making">
        Your personal data is not processed automatically, and is not
        subject to profiling.
      </Section>

      <Section title="10. Changes to the privacy policy">
        We reserve the right to modify this policy. The current version
        is always available at the same URL. We will inform you of
        significant changes with appropriate notice.
      </Section>
    </>
  )
}
